import { isNumber } from 'lodash'
import React from 'react'
import styled from 'styled-components'

import {
  CardBodyContainer,
  CardBodyContent,
  CardHeaderContainer,
  ContainerCard,
  LineDivider,
} from '~/views/components/ProjectOverview/components/ProjectOverviewCard'
import Text from '~/views/components/ui/Text'
import WarningIconAndTooltip from '../WarningIconAndTooltip'
import ForecastedHoursContent from './components/ForecastedHoursContent'
import LoggedEstimatedHoursContent from './components/LoggedEstimatedHoursContent'
import { useStaticLabels } from 'shared/hooks/use-static-labels'

type ProjectOverviewHoursCardProps = {
  show: boolean
  loggedHours?: number | null
  estimatedHours?: number | null
  totalTasksWithUnestimatedHoursCount?: number | null
  forecastedHours?: number | null
}

export default function ProjectOverviewHoursCard(
  props: ProjectOverviewHoursCardProps,
) {
  const {
    loggedHours,
    estimatedHours,
    totalTasksWithUnestimatedHoursCount,
    forecastedHours,
    show,
  } = props

  if (!show) {
    return null
  }

  return (
    <ContainerCard data-component-name={'hours-card'}>
      <HoursCardHeader
        totalTasksWithUnestimatedHoursCount={
          totalTasksWithUnestimatedHoursCount
        }
      />

      <HoursCardBody
        loggedHours={loggedHours}
        estimatedHours={estimatedHours}
        totalTasksWithUnestimatedHoursCount={
          totalTasksWithUnestimatedHoursCount
        }
        forecastedHours={forecastedHours}
      />
    </ContainerCard>
  )
}

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: auto;
`

type HoursCardHeaderProps = {
  totalTasksWithUnestimatedHoursCount?: number | null
}

function HoursCardHeader({
  totalTasksWithUnestimatedHoursCount,
}: HoursCardHeaderProps) {
  const [getCommonLabel, getProjectOverviewLabel] = useStaticLabels([
    'common',
    'projectOverview',
  ])

  const hasMissingTasks =
    isNumber(totalTasksWithUnestimatedHoursCount) &&
    totalTasksWithUnestimatedHoursCount > 0
  const singleMissingTask =
    hasMissingTasks && totalTasksWithUnestimatedHoursCount === 1

  return (
    <CardHeaderContainer>
      <HeaderContent>
        <Text
          data-component-name={'hours-title'}
          weight={'bold'}
          size={'large'}
        >
          {getCommonLabel('hours')}
        </Text>
        {hasMissingTasks && (
          <WarningIconAndTooltip iconStyle={{ top: '0' }}>
            <div
              className={'p-0 text-center'}
              data-component-name={'logged-estimated-hours-tooltip-container'}
            >
              <span className={'message m-0'} style={{ color: '#656565' }}>
                {getProjectOverviewLabel(
                  'projectOverviewThisProjectHasOneOrMoreTasksWithoutEstimates',
                )}{' '}
                {
                  <span className={'font-bold'}>
                    ({totalTasksWithUnestimatedHoursCount}
                    {!singleMissingTask
                      ? getCommonLabel('tasks')
                      : getCommonLabel('task')}
                    )
                  </span>
                }
                <span className={'block'}>
                  {getProjectOverviewLabel(
                    'projectOverviewThisMayAffectTheAccuracyOfForecasting',
                  )}
                </span>
              </span>
            </div>
          </WarningIconAndTooltip>
        )}
      </HeaderContent>
    </CardHeaderContainer>
  )
}

type HoursCardBodyProps = {
  estimatedHours?: number | null
  loggedHours?: number | null
  totalTasksWithUnestimatedHoursCount?: number | null
  forecastedHours?: number | null
}

function HoursCardBody({
  loggedHours,
  estimatedHours,
  totalTasksWithUnestimatedHoursCount,
  forecastedHours,
}: HoursCardBodyProps) {
  return (
    <CardBodyContainer>
      <CardBodyContent>
        <LoggedEstimatedHoursContent
          loggedHours={loggedHours}
          estimatedHours={estimatedHours}
          totalTasksWithUnestimatedHoursCount={
            totalTasksWithUnestimatedHoursCount
          }
        />
        <LineDivider />
        <ForecastedHoursContent
          forecastedHours={forecastedHours}
          estimatedHours={estimatedHours}
        />
      </CardBodyContent>
    </CardBodyContainer>
  )
}
